import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Download, Trash2, FileSignature } from "lucide-react";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import api, { apiError } from "../lib/api";
import { formatDate } from "../lib/utils-format";

const STATUS_STYLE = {
  Active: "bg-emerald-500/15 text-emerald-500",
  Inactive: "bg-secondary text-muted-foreground",
};

function UploadDialog({ open, onOpenChange, types, statuses, onUploaded }) {
  const blank = { name: "", contract_type: "Purchase Agreement", status: "Active", effective_date: "", notes: "" };
  const [form, setForm] = useState(blank);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setForm(blank); setFile(null); } }, [open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please choose a file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      await api.post("/contracts/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Contract uploaded");
      onOpenChange(false);
      onUploaded();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="contract-upload-dialog">
        <DialogHeader><DialogTitle>Upload Contract</DialogTitle><DialogDescription>Add a contract to the library and store the source file.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Contract Name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Land Purchase Agreement" data-testid="contract-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.contract_type} onValueChange={(v) => set("contract_type", v)}>
                <SelectTrigger data-testid="contract-type"><SelectValue /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="contract-status"><SelectValue /></SelectTrigger>
                <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Effective Date</Label>
            <Input type="date" value={form.effective_date} onChange={(e) => set("effective_date", e.target.value)} data-testid="contract-effective-date" />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="contract-notes" />
          </div>
          <div>
            <Label>File</Label>
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="contract-file" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="contract-upload-submit">{busy ? "Uploading…" : "Upload"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Contracts() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ types: [], statuses: [] });
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(() => {
    setLoading(true);
    const q = statusFilter !== "all" ? `?status=${encodeURIComponent(statusFilter)}` : "";
    api.get(`/contracts${q}`)
      .then((r) => { setItems(r.data.items || []); setMeta({ types: r.data.types || [], statuses: r.data.statuses || [] }); })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const download = async (c) => {
    try {
      const res = await api.get(`/contracts/${c.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = c.original_filename || c.name || "contract";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/contracts/${toDelete.id}`); toast.success("Contract removed"); setToDelete(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const columns = [
    { key: "status", label: "Status", render: (c) => (
      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[c.status] || "bg-secondary text-muted-foreground"}`} data-testid={`contract-status-${c.id}`}>{c.status || "—"}</span>
    ) },
    { key: "name", label: "Contract", render: (c) => <span className="font-medium text-foreground">{c.name || c.original_filename || "—"}</span> },
    { key: "contract_type", label: "Type", render: (c) => c.contract_type || "—" },
    { key: "effective_date", label: "Effective", render: (c) => c.effective_date ? formatDate(c.effective_date) : "—" },
    { key: "uploaded_by_name", label: "Uploaded By", render: (c) => c.uploaded_by_name || "—" },
    { key: "actions", label: "", render: (c) => (
      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => download(c)} data-testid={`contract-download-${c.id}`} title="Download"><Download className="h-4 w-4" /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setToDelete(c)} data-testid={`contract-delete-${c.id}`} title="Delete"><Trash2 className="h-4 w-4" /></Button>
      </div>
    ) },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="contracts-page">
      <PageHeader
        title="Contracts"
        count={items.length}
        subtitle="Store and manage purchase agreements, options, and legal documents"
        actions={<Button onClick={() => setUploadOpen(true)} data-testid="new-contract-btn"><Plus className="h-4 w-4 mr-1.5" />New Contract</Button>}
      />

      <div className="flex gap-3 mb-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[200px]" data-testid="contract-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!loading && items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-12 text-center">
          <FileSignature className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground mb-3">No contracts yet. Upload your first agreement to start tracking.</p>
          <Button onClick={() => setUploadOpen(true)} data-testid="empty-add-contract-btn"><Plus className="h-4 w-4 mr-1.5" />Upload Contract</Button>
        </div>
      ) : (
        <DataTable columns={columns} items={items} loading={loading} emptyLabel="No contracts found" />
      )}

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} types={meta.types} statuses={meta.statuses} onUploaded={load} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this contract?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.name} will be permanently removed from the library.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-contract">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
